import { ButtonHTMLAttributes, ReactNode } from 'react';

import { theme } from 'shared/styles/theme';
import { Spinner } from 'shared/ui/Spinner';

import { Typography } from './Typography';

import styled from '@emotion/styled';

export type ButtonVariant = 'default' | 'yellow' | 'outlined';

const ButtonStyled = styled('button')<{
  isLoading?: boolean;
  isWithoutBorder?: boolean;
  variant?: ButtonVariant;
  width?: string;
}>(({ isLoading, isWithoutBorder, variant, width }) => {
  let styles: any = {
    cursor: isLoading ? 'default' : 'pointer',
    border: !isWithoutBorder ? `${theme.borders.md} ${theme.colors.black}` : 'none',
    borderRadius: 12,
    padding: `${theme.space[4]} ${theme.space[5]}`,
    width: width ? width : 'fit-content',
    transition: '.3s',
    color: theme.colors.white,
    backgroundColor: theme.colors.black,
    '&:hover': {
      color: isLoading ? 'auto' : theme.colors.black,
      background: isLoading ? 'auto' : theme.colors.white,
    },
    '&:disabled': {
      background: theme.colors.gray100,
      border: 'none',
      color: theme.colors.black,
      cursor: 'not-allowed',
    },
  };

  if (variant === 'yellow') {
    styles = {
      ...styles,
      background: theme.colors.yellow700,
      color: theme.colors.black,
      border: 'none',
      '&:hover': {
        background: theme.colors.yellow800,
        color: theme.colors.black,
      },
    };
  }
  if (variant === 'outlined') {
    styles = {
      ...styles,
      background: 'white',
      color: theme.colors.black,
      border: `1px solid ${theme.colors.black200}`,
      '&:hover': {
        background: theme.colors.black100,
        border: `1px solid ${theme.colors.black300}`,
        color: theme.colors.black,
      },
    };
  }

  return styles;
});

type Props = {
  children: ReactNode;
  isLoading?: boolean;
  isLoadingWithSpinner?: boolean;
  fontSize?: string | number;
  isWithoutBorder?: boolean;
  variant?: ButtonVariant;
  width?: string;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = ({
  children,
  isLoading,
  isLoadingWithSpinner = undefined,
  fontSize = theme.fontSizes['17'],
  isWithoutBorder = false,
  variant = 'default',
  width,
  ...rest
}: Props) => (
  <ButtonStyled
    isLoading={isLoading}
    width={width}
    {...rest}
    type={rest.type || 'button'}
    disabled={isLoading || isLoadingWithSpinner || rest.disabled}
    isWithoutBorder={isWithoutBorder}
    variant={variant}
  >
    <Typography fontWeight={theme.fontWeights.medium} fontSize={fontSize} as='div'>
      {isLoadingWithSpinner || isLoading ? <Spinner /> : children}
    </Typography>
  </ButtonStyled>
);
